import { Link } from "react-router-dom";
import { Stack, Card, Typography, CardContent, Box } from "@material-ui/core";
import { grey } from "@material-ui/core/colors";
import {
  getFullAddress,
  clampLineStyle,
  getListingMatchAdditionalDetails
} from "utils/listings/display";
import { removeNumberFromAddress } from "utils/listings/parser";
import { formatNumber } from "utils/Haim/utils";
import { CarouselCard } from "components/carousel/CarouselCard";
import { ImagePreview } from "../../../../@types/listings/imageData";
import RoomsFloorAndSqrMtr from "components/_dashboard/listingsMatch/RoomsFloorAndSqrMtr";
import { Image } from "components/Image";
import useSettings from "hooks/useSettings";
import { ListingCardLikes } from "../../listingsMatch/ListingCardLikes";
import { ShareOutlined } from "@material-ui/icons";
import { IconButton } from "@material-ui/core";
import { shareLink, preventBubbling } from "utils/listings/helper";
import type {
  ProcessedListingData,
  FavoriteListingsOfContact
} from "./../../../../@types/sharedSchema";

export type ListingCardProps = {
  id: string;
  city: string;
  rooms: null | number;
  floor: null | number;
  price: number;
  address: string;
  pictures: null | ImagePreview[];
  createdTime?: Date;
  description: string;
  listingLink: string;
  neighborhood: string;
  sizeOfPlotM2: null | number;
  sizeOfPropertyM2: null | number;
  filterHiddenPictures?: boolean;
  favoriteListingsOfContact?: FavoriteListingsOfContact;
};

export function processedListingDataToListingCardProps(
  listing: ProcessedListingData,
  listingLink: string
): ListingCardProps {
  const listingData: ListingCardProps = {
    listingLink: listingLink,
    id: listing.id,
    city: listing.address.city,
    price: listing.price,
    rooms: listing.roomsCount,
    floor: listing.address.floor,
    address: listing.address.street,
    pictures: listing.imageUrls?.map((url) => ({ src: url, alt: "", id: url })) ?? [],
    createdTime: listing.createdTime,
    description: listing.description,
    sizeOfPlotM2: listing.sizeOfPlotM2,
    neighborhood: listing.address.neighborhood,
    sizeOfPropertyM2: listing.squareMeter
  };

  return listingData;
}

export function ListingCard({
  id,
  city,
  price,
  rooms,
  floor,
  address,
  pictures,
  createdTime,
  description,
  listingLink,
  neighborhood,
  sizeOfPlotM2,
  sizeOfPropertyM2,
  filterHiddenPictures,
  favoriteListingsOfContact
}: ListingCardProps): JSX.Element {
  const { themeDirection } = useSettings();

  const isRTL = themeDirection === "rtl";

  const parsedAddressWithoutNumber = removeNumberFromAddress(address);

  const fullAddress = getFullAddress({ address: parsedAddressWithoutNumber, neighborhood, city });

  const handleShare = shareLink({
    title: fullAddress,
    url: `${window.location.origin}${listingLink}`
  });

  const isImagesHidden = favoriteListingsOfContact?.listingIdsWithPicturesHidden?.includes(id);

  const last14DaysDate = new Date();

  last14DaysDate.setDate(last14DaysDate.getDate() - 14);

  // Check if the listing is new, i.e. created in the last 14 days
  const isNewListing = createdTime && createdTime > last14DaysDate;

  const { hiddenPictures, descriptionOverride } = getListingMatchAdditionalDetails({
    listingId: id,
    description: description,
    favoriteListingsOfContact: favoriteListingsOfContact
  });

  return (
    <Link style={{ textDecoration: "none" }} to={listingLink}>
      <Box
        sx={{
          height: "100%",
          position: "relative",
          transition: "transform 200ms",
          "&:hover": {
            "@media (min-width:600px)": {
              transform: "translateY(-8px)"
            }
          }
        }}
      >
        {isNewListing && (
          <Image
            sx={{
              position: "absolute",
              top: { xs: -12, sm: -20 },
              width: { xs: 80, sm: 128 },
              height: { xs: 80, sm: 128 },
              zIndex: 1,
              ...(isRTL ? { left: { xs: -12, sm: -20 } } : { right: { xs: -12, sm: -20 } })
            }}
            src="/static/_haim/new_listing.png"
          />
        )}
        <Card
          sx={{
            height: "100%",
            cursor: "pointer",
            "& > *:first-child": {
              height: { xs: 180, sm: 208 },
              overflow: "hidden",
              backgroundColor: "background.neutral"
            }
          }}
          variant="outlined"
        >
          <CarouselCard
            cropTopImage
            pictures={pictures}
            imagesHidden={isImagesHidden}
            hiddenPictures={hiddenPictures}
            filterHiddenPictures={filterHiddenPictures}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
            <Stack gap={2}>
              <Stack gap={0.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="start" gap={0.5}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        display: "-webkit-box",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        lineHeight: 1.4
                      }}
                      variant="h6"
                      fontWeight={500}
                      title={fullAddress}
                    >
                      {[
                        { text: parsedAddressWithoutNumber, color: grey[700] },
                        { text: neighborhood, color: grey[600] },
                        { text: city, color: grey[500] }
                      ]
                        .filter((part) => part.text)
                        .map((part, idx) => (
                          <Typography
                            key={idx}
                            sx={{ color: part.color, fontSize: "inherit" }}
                            display="inline"
                            variant="h6"
                            component="span"
                            fontWeight={500}
                          >
                            {idx > 0 ? ", " : ""}
                            {part.text}
                          </Typography>
                        ))}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
                    <IconButton
                      size="small"
                      onClick={preventBubbling(handleShare)}
                    >
                      <ShareOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                    {favoriteListingsOfContact && favoriteListingsOfContact.contactId && (
                      <ListingCardLikes
                        small
                        listingId={id}
                        favoriteListingsOfContact={favoriteListingsOfContact}
                      />
                    )}
                  </Stack>
                </Stack>
                <Typography variant="h5" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                  ₪{formatNumber(price)}
                </Typography>
              </Stack>
              <Stack gap={1}>
                {descriptionOverride && (
                  <Typography sx={clampLineStyle(3)} variant="body2" title={descriptionOverride}>
                    {descriptionOverride}
                  </Typography>
                )}
                <RoomsFloorAndSqrMtr
                  floor={floor}
                  roomsCount={rooms}
                  plotSizeM2={sizeOfPlotM2}
                  areaSizeM2={sizeOfPropertyM2}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Link>
  );
}
